<template>
  <div class="multi-step-form">
    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <div class="loading-spinner"></div>
      <p>Loading form...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="error-container">
      <div class="error-icon">⚠️</div>
      <h3>Error Loading Form</h3>
      <p>{{ error }}</p>
      <button @click="get_complete_form" class="retry-btn">
        Retry
      </button>
    </div>

    <!-- Empty State -->
    <div v-else-if="!formData || !formData.form_main_sections" class="empty-state">
      <p>No form data available.</p>
    </div>

    <!-- Form Content -->
    <div v-else>
      <!-- Timeline Titles -->
      <div class="onboarding-timeline-titles d-flex justify-content-center mb-2"
           v-if="sortedMainSections && sortedMainSections.length > 1">
        <template v-for="(section, index) in sortedMainSections" :key="'title-' + index">
          <div class="onboarding-timeline-title-wrapper text-center mx-2">
            <span class="onboarding-timeline-title">{{ section.main_section.name }}</span>
          </div>
        </template>
      </div>

      <!-- Timeline -->
      <div class="onboarding-timeline-wrapper d-flex justify-content-center align-items-center"
           v-if="sortedMainSections && sortedMainSections.length > 1">
        <template v-for="(section, index) in sortedMainSections" :key="'step-' + index">
          <!-- Step circle -->
          <div class="onboarding-timeline-step d-flex flex-column align-items-center">
            <div class="onboarding-timeline-circle"
                 :class="{
                   completed: index < currentSectionIndex,
                   active: index === currentSectionIndex,
                   upcoming: index > currentSectionIndex
                 }">
            </div>
          </div>

          <!-- Line between steps -->
          <div v-if="index < sortedMainSections.length - 1"
               class="onboarding-timeline-line"
               :class="{
                 completed: index < currentSectionIndex,
                 upcoming: index >= currentSectionIndex
               }">
          </div>
        </template>
      </div>

      <!-- Form Header -->
      <div class="form-header-card mt-4">
        <h1>{{ formData.form_name }}</h1>
        <p v-if="formData.form_description" class="form-description">{{ formData.form_description }}</p>
        <div class="form-meta">
          <span class="form-status" :class="{ 'active': formData.is_active, 'inactive': !formData.is_active }">
            {{ formData.is_active ? 'Active' : 'Inactive' }}
          </span>
          <span class="form-type">Type: {{ formData.form_type }}</span>
        </div>
      </div>

      <!-- Multi-step Form Sections -->
      <div class="forms-container mt-4">
        <div class="carousel-container" ref="carousel">
          <div v-for="(mainSection, section_index) in sortedMainSections"
               :key="'section-' + section_index"
               class="form-wrapper"
               :class="{ active: section_index === currentSectionIndex }"
               :data-index="section_index">

            <div class="form-card mt-0 shadow-lg">
              <!-- Section Header -->
              <div class="section-header fw-bold mt-3 text-xs d-flex justify-content-between align-items-center">
                <div class="section-title d-flex align-items-center gap-2 fw-bold text-x">
                  {{ mainSection.main_section.name }}
                  <i class="bi bi-info-circle fs-5 text-primary"
                     v-if="mainSection.main_section.description"
                     :title="mainSection.main_section.description"></i>
                </div>
                <div class="d-flex align-items-center gap-2">
                  <span class="status-badge text-xxs border-1"
                        :class="isSectionCompleted(section_index) ? 'verified' : 'pending'">
                    {{ isSectionCompleted(section_index) ? 'Completed' : 'Incomplete' }}
                  </span>
                </div>
              </div>
              <hr class="mt-0 fw-bold border border-dark" />

              <!-- Sub-sections and Questions -->
              <div class="form-fields-container p-3">
                <div v-for="subSection in mainSection.form_main_sections" :key="subSection.id" class="sub-section">
                  <h3 v-if="subSection.section.name !== mainSection.main_section.name"
                      class="sub-section-title text-xs fw-bold mb-3">
                    {{ subSection.section.name }}
                  </h3>

                  <!-- Questions -->
                  <div v-for="sectionQuestion in sortedQuestions(subSection.sectionquestion_set)"
                       :key="sectionQuestion.id"
                       class="question-container mb-4 p-3 rounded shadow-sm"
                       :class="{
                         'dependent-question': sectionQuestion.question.is_assigned_as_dependent,
                         'bg-light': sectionQuestion.question.is_assigned_as_dependent
                       }">

                    <!-- Question Label -->
                    <label class="question-label fw-medium text-xs text-start d-block mb-2">
                      {{ sectionQuestion.question.question_text }}
                      <span v-if="sectionQuestion.is_mandatory" class="mandatory-indicator text-danger fs-5">*</span>
                      <span v-if="sectionQuestion.question.is_assigned_as_dependent"
                            class="dependent-indicator text-primary ms-2">
                        (Dependent)
                      </span>
                    </label>

                    <!-- Question Input based on type -->
                    <div class="question-input">
                      <!-- Short Text (id: 1) -->
                      <input v-if="sectionQuestion.question.question_type.id === 1"
                             type="text"
                             v-model="formValues[sectionQuestion.question.id]"
                             :placeholder="`Enter ${sectionQuestion.question.question_text.toLowerCase()}`"
                             class="form-control form-control-lg text-xxs bg-light border-1 py-1 rounded-lg"
                             :required="sectionQuestion.is_mandatory"
                             :class="{ 'border-danger': validationErrors[sectionQuestion.question.id] }" />

                      <!-- Long Text (id: 2) -->
                      <textarea v-else-if="sectionQuestion.question.question_type.id === 2"
                                v-model="formValues[sectionQuestion.question.id]"
                                :placeholder="`Enter ${sectionQuestion.question.question_text.toLowerCase()}`"
                                class="form-control form-control-lg text-xxs bg-light border-1 py-1 rounded-lg"
                                rows="3"
                                :required="sectionQuestion.is_mandatory"
                                :class="{ 'border-danger': validationErrors[sectionQuestion.question.id] }"></textarea>

                      <!-- Single Select (id: 3) -->
                      <div v-else-if="sectionQuestion.question.question_type.id === 3"
                           class="radio-group mt-2 p-2 rounded"
                           :class="{ 'border border-danger': validationErrors[sectionQuestion.question.id] }">
                        <div v-for="choice in sectionQuestion.question.multiselect_set"
                             :key="choice.id"
                             class="form-check mb-2">
                          <input class="form-check-input"
                                 type="radio"
                                 :name="'single-select-' + sectionQuestion.question.id"
                                 :id="`choice-${choice.id}`"
                                 v-model="formValues[sectionQuestion.question.id]"
                                 :value="choice.id"
                                 :required="sectionQuestion.is_mandatory" />
                          <label class="form-check-label text-xxs" :for="`choice-${choice.id}`">
                            {{ choice.choice }}
                          </label>
                        </div>
                      </div>

                      <!-- Multi Select (id: 4) -->
                      <div v-else-if="sectionQuestion.question.question_type.id === 4"
                           class="mt-2 p-2 rounded"
                           :class="{ 'border border-danger': validationErrors[sectionQuestion.question.id] }">
                        <div v-for="choice in sectionQuestion.question.multiselect_set"
                             :key="choice.id"
                             class="form-check mb-2">
                          <input type="checkbox"
                                 :id="`multi-choice-${choice.id}`"
                                 :value="choice.id"
                                 v-model="multiSelectValues[sectionQuestion.question.id]"
                                 class="form-check-input" />
                          <label :for="`multi-choice-${choice.id}`" class="form-check-label text-xxs">
                            {{ choice.choice }}
                          </label>
                        </div>
                      </div>

                      <!-- File Upload (id: 5) -->
                      <div v-else-if="sectionQuestion.question.question_type.id === 5" class="file-upload-container">
                        <div v-if="fileNames[sectionQuestion.question.id]" class="d-flex align-items-center gap-2 mb-2">
                          <i class="bi bi-file-earmark-text text-primary fs-5"></i>
                          <span class="text-xs">{{ fileNames[sectionQuestion.question.id] }}</span>
                          <button class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark ms-2"
                                  type="button"
                                  @click="removeFile(sectionQuestion.question.id)">
                            Remove
                          </button>
                        </div>
                        <div v-else>
                          <input type="file"
                                 class="form-control form-control-lg text-xxs bg-light border-1 py-1 rounded-lg"
                                 @change="handleFileUpload($event, sectionQuestion.question.id)"
                                 :required="sectionQuestion.is_mandatory" />

                          <!-- Comment field for file uploads -->
                          <div v-if="sectionQuestion.is_mandatory && !fileNames[sectionQuestion.question.id]"
                               class="mt-2">
                            <label class="text-xs" :for="`file-comment-${sectionQuestion.question.id}`">
                              Please explain why you cannot upload this file:
                            </label>
                            <textarea :id="`file-comment-${sectionQuestion.question.id}`"
                                      class="form-control form-control-lg text-xs bg-light border-1 py-1 rounded-lg mt-1"
                                      v-model="fileComments[sectionQuestion.question.id]"
                                      rows="2"
                                      placeholder="Provide a reason..."></textarea>
                          </div>
                        </div>
                      </div>

                      <!-- Number (id: 6) -->
                      <input v-else-if="sectionQuestion.question.question_type.id === 6"
                             type="number"
                             v-model="formValues[sectionQuestion.question.id]"
                             :placeholder="`Enter ${sectionQuestion.question.question_text.toLowerCase()}`"
                             class="form-control form-control-lg text-xxs bg-light border-1 py-1 rounded-lg"
                             :required="sectionQuestion.is_mandatory"
                             :class="{ 'border-danger': validationErrors[sectionQuestion.question.id] }" />

                      <!-- Date (id: 7) -->
                      <input v-else-if="sectionQuestion.question.question_type.id === 7"
                             type="date"
                             v-model="formValues[sectionQuestion.question.id]"
                             class="form-control form-control-lg text-xxs bg-light border-1 py-1 rounded-lg"
                             :required="sectionQuestion.is_mandatory"
                             :class="{ 'border-danger': validationErrors[sectionQuestion.question.id] }" />

                      <!-- Facial Verification (id: 8) -->
                      <div v-else-if="sectionQuestion.question.question_type.id === 8"
                           class="facial-verification-container">
                        <div v-if="facialVerificationStatus[sectionQuestion.question.id]"
                             class="d-flex align-items-center gap-2 mb-2">
                          <i class="bi bi-check-circle-fill text-success fs-5"></i>
                          <span class="text-xs">{{ facialVerificationStatus[sectionQuestion.question.id] }}</span>
                        </div>
                        <button type="button"
                                class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark"
                                @click="startFacialVerification(sectionQuestion.question.id)"
                                :disabled="facialVerificationStatus[sectionQuestion.question.id] === 'Verified'">
                          {{ facialVerificationStatus[sectionQuestion.question.id] ? 'Re-verify' : 'Start Facial Verification' }}
                        </button>
                        <p class="facial-verification-hint text-xxs text-muted mt-1">
                          This will open your camera for facial recognition
                        </p>
                      </div>

                      <!-- Default text input for unknown types -->
                      <input v-else
                             type="text"
                             v-model="formValues[sectionQuestion.question.id]"
                             :placeholder="`Enter ${sectionQuestion.question.question_text.toLowerCase()}`"
                             class="form-control form-control-lg text-xxs bg-light border-1 py-1 rounded-lg"
                             :required="sectionQuestion.is_mandatory"
                             :class="{ 'border-danger': validationErrors[sectionQuestion.question.id] }" />
                    </div>

                    <!-- Question Type Indicator -->
                    <small class="question-type-hint text-xxs text-muted mt-1 d-block">
                      <i class="bi bi-info-circle me-1"></i>
                      {{ getQuestionTypeText(sectionQuestion.question.question_type.id) }}
                      <span v-if="sectionQuestion.question.multiselect_set && sectionQuestion.question.multiselect_set.length > 0">
                        • {{ sectionQuestion.question.multiselect_set.length }} options
                      </span>
                    </small>

                    <!-- Validation Error -->
                    <div v-if="validationErrors[sectionQuestion.question.id]"
                         class="text-danger text-xxs mt-1">
                      {{ validationErrors[sectionQuestion.question.id] }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- Section Navigation Buttons -->
              <div class="d-flex justify-content-between mt-4 pt-3 border-top">
                <button v-if="section_index > 0"
                        class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow-lg text-dark fw-bold"
                        @click="goToPreviousSection">
                  <i class="bi bi-chevron-left me-1"></i> Previous
                </button>
                <div v-else></div> <!-- Empty div for spacing -->

                <button v-if="section_index < sortedMainSections.length - 1"
                        class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow-lg text-dark fw-bold"
                        @click="goToNextSection">
                  Next <i class="bi bi-chevron-right ms-1"></i>
                </button>
                <button v-else
                        class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow-lg text-dark fw-bold"
                        @click="submitForm"
                        :disabled="submitting">
                  {{ submitting ? 'Submitting...' : 'Submit Form' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { GET_COMPLETE_FORM, get_headers, get_project_id } from '@/utils/constants/constants.js';
import axios from 'axios';

export default {
  name: 'OngoingComplianceComponent',
  props: {
    form_type: {
      type: String,
      required: true
    },
    form_id: {
      type: [String, Number],
      default: null
    },
    entity_profile_id: {
      type: [String, Number],
      default: null
    }
  },
  data() {
    return {
      forms: null,
      headers: null,
      project_id: null,
      entity_type_id: null,
      loading: false,
      error: null,
      formData: {},
      formValues: {},
      multiSelectValues: {},
      fileNames: {},
      fileComments: {},
      facialVerificationStatus: {},
      submitting: false,
      currentSectionIndex: 0,
      validationErrors: {},
      sectionValidations: {}
    }
  },
  computed: {
    // Sort main sections by order_number
    sortedMainSections() {
      if (!this.formData.form_main_sections) return []
      return [...this.formData.form_main_sections].sort((a, b) =>
        (a.order_number || 0) - (b.order_number || 0)
      )
    }
  },
  mounted() {
    this.headers = get_headers()
    this.project_id = get_project_id()
    this.entity_type_id = sessionStorage.getItem('entity_type_id')
    this.get_complete_form()
  },
  methods: {
    get_complete_form() {
      this.loading = true
      this.error = null

      const params = {
        entity_type_id: this.entity_type_id,
        form_id: this.form_id,
        form_type: this.form_type || "Compliance Form",
      }

      axios.get(GET_COMPLETE_FORM, { params: params, headers: this.headers })
        .then(response => {
          if (response.data.status === 'success' && response.data.data && response.data.data.length > 0) {
            this.formData = response.data.data[0]
            this.initializeFormValues()
          } else {
            this.formData = {}
            this.error = 'No form data available'
          }
        })
        .catch(error => {
          this.show_error('Error fetching forms:', error)
          this.error = 'Failed to load form. Please try again.'
          this.formData = {}
        })
        .finally(() => {
          this.loading = false
        })
    },

    // Initialize form values based on question types
    initializeFormValues() {
      if (!this.formData.form_main_sections) return

      this.formData.form_main_sections.forEach(mainSection => {
        mainSection.form_main_sections.forEach(subSection => {
          subSection.sectionquestion_set.forEach(sectionQuestion => {
            const question = sectionQuestion.question

            // Initialize based on question type
            if (question.question_type.id === 4) {
              // Multi-select: initialize as empty array
              this.multiSelectValues = {
                ...this.multiSelectValues,
                [question.id]: []
              }
            } else {
              // Other types: initialize as empty string
              this.formValues = {
                ...this.formValues,
                [question.id]: ''
              }
            }
          })
        })
      })
    },

    // Sort questions by order_number
    sortedQuestions(questions) {
      if (!questions) return []
      return [...questions].sort((a, b) =>
        (a.order_number || 0) - (b.order_number || 0)
      )
    },

    // Get human-readable question type text
    getQuestionTypeText(typeId) {
      const typeMap = {
        1: 'Short text',
        2: 'Long text',
        3: 'Single select',
        4: 'Multiple select',
        5: 'File upload',
        6: 'Number',
        7: 'Date',
        8: 'Facial verification'
      }
      return typeMap[typeId] || 'Text answer'
    },

    // Check if a section is completed
    isSectionCompleted(sectionIndex) {
      const mainSection = this.sortedMainSections[sectionIndex]
      if (!mainSection) return false

      let hasQuestions = false
      let completedQuestions = 0

      mainSection.form_main_sections.forEach(subSection => {
        subSection.sectionquestion_set.forEach(sectionQuestion => {
          hasQuestions = true
          const question = sectionQuestion.question
          const value = this.formValues[question.id]
          const multiValues = this.multiSelectValues[question.id]
          const fileName = this.fileNames[question.id]

          if (question.question_type.id === 4) {
            // Multi-select
            if (multiValues && multiValues.length > 0) {
              completedQuestions++
            }
          } else if (question.question_type.id === 5) {
            // File upload
            if (fileName || this.fileComments[question.id]) {
              completedQuestions++
            }
          } else if (question.question_type.id === 8) {
            // Facial verification
            if (this.facialVerificationStatus[question.id] === 'Verified') {
              completedQuestions++
            }
          } else {
            // Other types
            if (value && value.toString().trim() !== '') {
              completedQuestions++
            }
          }
        })
      })

      return hasQuestions && completedQuestions > 0
    },

    // Validate current section
    validateCurrentSection() {
      const errors = {}
      const mainSection = this.sortedMainSections[this.currentSectionIndex]

      if (!mainSection) return errors

      mainSection.form_main_sections.forEach(subSection => {
        subSection.sectionquestion_set.forEach(sectionQuestion => {
          if (sectionQuestion.is_mandatory) {
            const question = sectionQuestion.question
            const value = this.formValues[question.id]
            const multiValues = this.multiSelectValues[question.id]
            const fileName = this.fileNames[question.id]
            const fileComment = this.fileComments[question.id]

            let isValid = false

            if (question.question_type.id === 4) {
              // Multi-select validation
              isValid = multiValues && multiValues.length > 0
            } else if (question.question_type.id === 5) {
              // File upload validation
              isValid = fileName || (fileComment && fileComment.trim() !== '')
            } else if (question.question_type.id === 8) {
              // Facial verification
              isValid = this.facialVerificationStatus[question.id] === 'Verified'
            } else {
              // Other types validation
              isValid = value && value.toString().trim() !== ''
            }

            if (!isValid) {
              errors[question.id] = 'This field is required'
            }
          }
        })
      })

      this.validationErrors = errors
      return Object.keys(errors).length === 0
    },

    // Handle file upload
    handleFileUpload(event, questionId) {
      const file = event.target.files[0]
      if (file) {
        this.formValues = {
          ...this.formValues,
          [questionId]: file
        }
        this.fileNames = {
          ...this.fileNames,
          [questionId]: file.name
        }
        // Clear validation error for this field
        if (this.validationErrors[questionId]) {
          const newErrors = { ...this.validationErrors }
          delete newErrors[questionId]
          this.validationErrors = newErrors
        }
      }
    },

    // Remove file
    removeFile(questionId) {
      const newFormValues = { ...this.formValues }
      delete newFormValues[questionId]
      this.formValues = newFormValues

      const newFileNames = { ...this.fileNames }
      delete newFileNames[questionId]
      this.fileNames = newFileNames

      const newFileComments = { ...this.fileComments }
      delete newFileComments[questionId]
      this.fileComments = newFileComments
    },

    // Start facial verification
    startFacialVerification(questionId) {
      // This is a placeholder for facial verification logic
      // In a real app, you would integrate with a facial recognition API
      this.facialVerificationStatus = {
        ...this.facialVerificationStatus,
        [questionId]: 'Starting...'
      }

      setTimeout(() => {
        // Simulate facial verification process
        this.facialVerificationStatus = {
          ...this.facialVerificationStatus,
          [questionId]: 'Verifying...'
        }

        setTimeout(() => {
          // Simulate successful verification
          this.facialVerificationStatus = {
            ...this.facialVerificationStatus,
            [questionId]: 'Verified'
          }
          this.formValues = {
            ...this.formValues,
            [questionId]: 'verified_' + Date.now()
          }

          // Clear validation error for this field
          if (this.validationErrors[questionId]) {
            const newErrors = { ...this.validationErrors }
            delete newErrors[questionId]
            this.validationErrors = newErrors
          }
        }, 2000)
      }, 1000)
    },

    // Navigate to next section
    goToNextSection() {
      if (this.validateCurrentSection()) {
        if (this.currentSectionIndex < this.sortedMainSections.length - 1) {
          this.currentSectionIndex++
          this.scrollToCurrentSection()
        }
      } else {
        alert('Please fill in all required fields before proceeding.')
      }
    },

    // Navigate to previous section
    goToPreviousSection() {
      if (this.currentSectionIndex > 0) {
        this.currentSectionIndex--
        this.scrollToCurrentSection()
      }
    },

    // Scroll to current section
    scrollToCurrentSection() {
      this.$nextTick(() => {
        const carousel = this.$refs.carousel
        if (carousel) {
          const activeSection = carousel.querySelector('.form-wrapper.active')
          if (activeSection) {
            activeSection.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
              inline: 'center'
            })
          }
        }
      })
    },

    // Submit form
    async submitForm() {
      if (!this.validateCurrentSection()) {
        alert('Please fill in all required fields in the current section before submitting.')
        return
      }

      // Validate all sections
      let allValid = true
      for (let i = 0; i < this.sortedMainSections.length; i++) {
        this.currentSectionIndex = i
        if (!this.validateCurrentSection()) {
          allValid = false
          break
        }
      }

      if (!allValid) {
        alert('Please fill in all required fields in all sections before submitting.')
        return
      }

      this.currentSectionIndex = this.sortedMainSections.length - 1

      this.submitting = true

      try {
        // Prepare form data for submission
        const submissionData = this.prepareSubmissionData()

        // Here you would make an API call to submit the form
        // Example:
        // const response = await axios.post('/api/submit-form', submissionData, { headers: this.headers })

        // For now, simulate API call
        await new Promise(resolve => setTimeout(resolve, 1000))

        alert('Form submitted successfully!')
        this.$emit('form-submitted', submissionData)

      } catch (error) {
        alert('Failed to submit form. Please try again.')
      } finally {
        this.submitting = false
      }
    },

    // Prepare data for submission
    prepareSubmissionData() {
      const submissionData = {
        form_id: this.formData.id,
        form_name: this.formData.form_name,
        entity_type_id: this.entity_type_id,
        project_id: this.project_id,
        responses: []
      }

      // Collect all responses
      this.formData.form_main_sections.forEach(mainSection => {
        mainSection.form_main_sections.forEach(subSection => {
          subSection.sectionquestion_set.forEach(sectionQuestion => {
            const question = sectionQuestion.question
            let responseValue

            if (question.question_type.id === 4) {
              // Multi-select values
              responseValue = this.multiSelectValues[question.id] || []
            } else if (question.question_type.id === 5) {
              // File - include both file name and comment if available
              responseValue = {
                file_name: this.fileNames[question.id] || '',
                comment: this.fileComments[question.id] || ''
              }
            } else if (question.question_type.id === 8) {
              // Facial verification
              responseValue = {
                status: this.facialVerificationStatus[question.id] || '',
                value: this.formValues[question.id] || ''
              }
            } else {
              // Other types
              responseValue = this.formValues[question.id] || ''
            }

            submissionData.responses.push({
              question_id: question.id,
              question_text: question.question_text,
              question_type_id: question.question_type.id,
              question_type: question.question_type.question_type,
              is_mandatory: sectionQuestion.is_mandatory,
              is_dependent: question.is_assigned_as_dependent,
              value: responseValue,
              section_question_id: sectionQuestion.id
            })
          })
        })
      })

      return submissionData
    }
  }
}
</script>

<style scoped>
/* Multi-step form container */
.multi-step-form {
  position: relative;
  min-height: 100vh;
  background-image: var(--client-bg, linear-gradient(135deg, #667eea 0%, #764ba2 100%));
  background-size: cover;
  background-position: center;
  padding: 20px;
  padding-bottom: 30px;
}

/* Form Header Card */
.form-header-card {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 15px;
  padding: 20px;
  margin: 20px auto;
  max-width: 800px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  text-align: center;
}

.form-header-card h1 {
  color: #2c3e50;
  margin-bottom: 10px;
  font-size: 32px;
  font-weight: 600;
}

.form-description {
  color: #7f8c8d;
  font-size: 18px;
  margin-bottom: 15px;
  line-height: 1.5;
}

.form-meta {
  display: flex;
  justify-content: center;
  gap: 20px;
  font-size: 14px;
}

.form-status {
  padding: 4px 12px;
  border-radius: 12px;
  font-weight: 500;
}

.form-status.active {
  background-color: #d4edda;
  color: #155724;
}

.form-status.inactive {
  background-color: #f8d7da;
  color: #721c24;
}

.form-type {
  color: #6c757d;
  font-style: italic;
}

/* Timeline Styles */
.onboarding-timeline-titles {
  margin-top: 30px;
}

.onboarding-timeline-title-wrapper {
  min-width: 120px;
  max-width: 150px;
}

.onboarding-timeline-title {
  font-size: 12px;
  font-weight: 600;
  color: #fff;
  background: rgba(255, 255, 255, 0.2);
  padding: 5px 10px;
  border-radius: 15px;
  display: inline-block;
}

.onboarding-timeline-wrapper {
  margin: 20px 0 30px 0;
  position: relative;
}

.onboarding-timeline-step {
  position: relative;
  z-index: 2;
}

.onboarding-timeline-circle {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 3px solid rgba(255, 255, 255, 0.3);
  background-color: transparent;
  transition: all 0.3s ease;
}

.onboarding-timeline-circle.completed {
  background-color: #28a745;
  border-color: #28a745;
}

.onboarding-timeline-circle.active {
  background-color: #007bff;
  border-color: #007bff;
  box-shadow: 0 0 0 5px rgba(0, 123, 255, 0.2);
}

.onboarding-timeline-circle.upcoming {
  background-color: transparent;
  border-color: rgba(255, 255, 255, 0.3);
}

.onboarding-timeline-line {
  height: 3px;
  width: 80px;
  background-color: rgba(255, 255, 255, 0.3);
  transition: all 0.3s ease;
}

.onboarding-timeline-line.completed {
  background-color: #28a745;
}

.onboarding-timeline-line.upcoming {
  background-color: rgba(255, 255, 255, 0.3);
}

/* Carousel Container */
.forms-container {
  padding: 3px;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  width: 100%;
  margin: 0 auto;
  overflow: hidden;
}

.carousel-container {
  display: flex;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scroll-padding: 0 50%;
  gap: 20px;
  padding: 20px;
  width: 100%;
  margin: 0 auto;
  max-width: 1200px;
  scroll-behavior: smooth;
}

.carousel-container::before,
.carousel-container::after {
  content: '';
  flex: 0 0 calc((100% - min(750px, 90vw)) / 2);
}

.carousel-container::-webkit-scrollbar {
  display: none;
}
.carousel-container {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

/* Form Wrapper */
.form-wrapper {
  flex: 0 0 auto;
  width: min(750px, 90vw);
  scroll-snap-align: center;
  transition: all 0.3s ease;
  filter: blur(3px);
  transform: scale(0.9);
  opacity: 0.7;
  margin: 0 auto;
}

.form-wrapper.active {
  filter: blur(0);
  transform: scale(1);
  opacity: 1;
  z-index: 5;
}

/* Form Card */
.form-card {
  border-radius: 15px;
  background: rgba(255, 255, 255, 0.98);
  padding: 20px;
  max-height: 80vh;
  width: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  margin: 0 auto;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

/* Section Header */
.section-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 12px 20px;
  border-radius: 10px;
  margin-bottom: 15px;
}

.section-title {
  color: white;
  font-size: 18px;
  margin: 0;
}

/* Status Badge */
.status-badge {
  padding: 4px 12px;
  border-radius: 20px;
  font-weight: 600;
  font-size: 12px;
}

.status-badge.verified {
  background-color: #d4edda;
  color: #155724;
  border: 1px solid #28a745;
}

.status-badge.pending {
  background-color: #fff3cd;
  color: #856404;
  border: 1px solid #ffc107;
}

/* Question Container */
.question-container {
  margin-bottom: 20px;
  padding: 15px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border-left: 4px solid #3498db;
  transition: all 0.3s ease;
}

.question-container:hover {
  background-color: #e9ecef;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.question-container.dependent-question {
  border-left-color: #9b59b6;
  background-color: #f3e6ff;
}

.question-label {
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #2c3e50;
  font-size: 14px;
}

.mandatory-indicator {
  color: #e74c3c;
  margin-left: 4px;
  font-weight: bold;
}

.dependent-indicator {
  color: #9b59b6;
  margin-left: 8px;
  font-size: 11px;
  font-style: italic;
}

/* Form Controls */
.form-control {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.3s ease;
  background-color: white;
}

.form-control:focus {
  border-color: #3498db;
  outline: none;
  box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.2);
}

.form-control::placeholder {
  color: #95a5a6;
}

/* Radio and Checkbox Groups */
.radio-group {
  background: white;
  border-radius: 6px;
  padding: 10px;
  border: 1px solid #dee2e6;
}

.form-check {
  margin-bottom: 8px;
}

.form-check-input:checked {
  background-color: #3498db;
  border-color: #3498db;
}

.form-check-label {
  margin-left: 5px;
  color: #495057;
}

/* File Upload */
.file-upload-container {
  background: white;
  border-radius: 6px;
  padding: 10px;
  border: 1px solid #dee2e6;
}

/* Facial Verification */
.facial-verification-container {
  background: white;
  border-radius: 6px;
  padding: 15px;
  border: 1px solid #dee2e6;
}

.facial-verification-btn {
  padding: 8px 16px;
  background-color: #3498db;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s ease;
  font-weight: 500;
}

.facial-verification-btn:hover:not(:disabled) {
  background-color: #2980b9;
  transform: translateY(-1px);
}

.facial-verification-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Question Type Hint */
.question-type-hint {
  color: #6c757d;
  font-size: 11px;
  margin-top: 5px;
  font-style: italic;
}

/* Buttons */
.btn {
  padding: 8px 20px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;
  min-width: 100px;
}

.btn-primary-dark {
  background-color: #2c3e50;
  color: white;
}

.btn-primary-dark:hover:not(:disabled) {
  background-color: #1a252f;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Loading, Error, Empty States */
.loading-container,
.error-container,
.empty-state {
  text-align: center;
  padding: 100px 20px;
  background-color: rgba(255, 255, 255, 0.95);
  border-radius: 15px;
  margin: 50px auto;
  max-width: 500px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-icon {
  font-size: 48px;
  margin-bottom: 20px;
  color: #e74c3c;
}

.error-container h3 {
  color: #e74c3c;
  margin-bottom: 10px;
}

.retry-btn {
  padding: 10px 25px;
  background-color: #3498db;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.3s ease;
}

.retry-btn:hover {
  background-color: #2980b9;
}

/* Responsive Design */
@media (max-width: 768px) {
  .multi-step-form {
    padding: 10px;
  }

  .form-header-card {
    padding: 15px;
    margin: 10px auto;
  }

  .form-header-card h1 {
    font-size: 24px;
  }

  .form-description {
    font-size: 14px;
  }

  .carousel-container {
    padding: 10px;
    gap: 10px;
  }

  .form-wrapper {
    width: 95vw;
  }

  .form-card {
    padding: 15px;
    max-height: 70vh;
  }

  .section-header {
    padding: 10px 15px;
  }

  .section-title {
    font-size: 16px;
  }

  .onboarding-timeline-title-wrapper {
    min-width: 80px;
  }

  .onboarding-timeline-title {
    font-size: 10px;
    padding: 3px 6px;
  }

  .onboarding-timeline-circle {
    width: 25px;
    height: 25px;
  }

  .onboarding-timeline-line {
    width: 40px;
  }

  .question-label {
    font-size: 13px;
  }

  .btn {
    padding: 6px 12px;
    font-size: 13px;
    min-width: 80px;
  }
}

/* Print Styles */
@media print {
  .multi-step-form {
    background: white !important;
  }

  .form-card {
    box-shadow: none !important;
    border: 1px solid #000 !important;
  }

  .btn {
    display: none !important;
  }

  .onboarding-timeline-wrapper,
  .onboarding-timeline-titles {
    display: none !important;
  }
}

/* Utility classes */
.text-xs { font-size: 12px; }
.text-xxs { font-size: 11px; }
.text-x { font-size: 16px; }
.fw-bold { font-weight: 600; }
.fw-medium { font-weight: 500; }
.rounded-small { border-radius: 4px; }
.rounded-lg { border-radius: 8px; }
.shadow-sm { box-shadow: 0 2px 4px rgba(0,0,0,0.1); }
.shadow-lg { box-shadow: 0 8px 16px rgba(0,0,0,0.15); }
.border-1 { border-width: 1px; }
.bg-light { background-color: #f8f9fa; }
</style>